import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  PropsWithChildren,
  useCallback,
} from "react";
import { useAudioVideo } from "amazon-chime-sdk-component-library-react";
import TranscriptEvent from "amazon-chime-sdk-js/build/transcript/TranscriptEvent";
import Transcript from "amazon-chime-sdk-js/build/transcript/Transcript";
import { extractRoomMemberData } from "@core/features/video";
import type { TranscriptionSegment } from "@core/features/video";
import { useVideoMeetingDetails, useVideoMeetingTranscription, useVideoSession, useUser } from "@platform/store";
import { toggleVideoMeetingTranscription, useVideoMeetingHeartbeat } from "@core/features/videoMeeting";
import type { VideoRoomHeartbeatResponse } from "@core/features/videoMeeting";
import { logVideoRoomError } from "../utils/logVideoRoomError";

export interface TranscriptionState {
  isTranscriptionDisabled: boolean;
  isTranscribing: boolean;
  segments: TranscriptionSegment[];
  currentSegment: TranscriptionSegment | null;
  lines: string[]; // For displaying transcript lines
  isLoading: boolean; // Loading state when starting transcription
  error: string | null; // Error state for transcription operations
}

interface TranscriptionContextType {
  state: TranscriptionState;
  toggleTranscription: () => void;
  isTranscriptionAvailable: boolean; // Whether transcription can be toggled
}

const TranscriptionContext = createContext<
  TranscriptionContextType | undefined
>(undefined);

/** Minimum confidence score for a transcript result to be accepted */
const TRANSCRIPTION_CONFIDENCE_THRESHOLD = 0.5;

/** Debounce interval for partial transcript updates (ms) */
const TRANSCRIPTION_PARTIAL_DEBOUNCE_MS = 300;

const initialState: TranscriptionState = {
  isTranscriptionDisabled: false,
  isTranscribing: false,
  segments: [],
  currentSegment: null,
  lines: [],
  isLoading: false,
  error: null,
};

export const TranscriptionProvider: React.FC<
  PropsWithChildren<{
    isTranscriptionDisabled: boolean;
  }>
> = ({ children, isTranscriptionDisabled }) => {
  const audioVideo = useAudioVideo();
  const { meetingId, meetingDetails } = useVideoMeetingDetails();
  const { transcriptionStatus, setTranscriptionStatus } = useVideoMeetingTranscription();
  const { userName } = useVideoSession();
  const user = useUser();
  const [state, setState] = useState<TranscriptionState>(initialState);
  const isToggling = useRef(false);

  const handleHeartbeat = useCallback(
    (response: VideoRoomHeartbeatResponse) => {
      setTranscriptionStatus(response.transcription_status);
    },
    [setTranscriptionStatus]
  );

  useVideoMeetingHeartbeat({
    meetingId: meetingId ? String(meetingId) : null,
    userName,
    user: user ? { id: user.id } : null,
    isEnabled: Boolean(meetingId),
    onHeartbeat: handleHeartbeat,
  });

  const lastPartialText = useRef<string | null>(null);
  const lastPartialUpdate = useRef<number>(0);

  // Check if transcription is available (meeting exists and user has access)
  const isTranscriptionAvailable = Boolean(meetingDetails?.id);

  // Derive isTranscribing from backend transcription status.
  // The toggle action calls the API, and the heartbeat confirms the state,
  // eliminating the two-source-of-truth problem.
  const derivedIsTranscribing = transcriptionStatus === 'on';

  useEffect(() => {
    if (!derivedIsTranscribing && state.isTranscribing) {
      // Transcription was turned off externally (by background job or another user)
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        segments: [],
        currentSegment: null,
        lines: [],
        error: null,
      }));
      lastPartialText.current = null;
      lastPartialUpdate.current = 0;
    } else if (derivedIsTranscribing && !state.isTranscribing) {
      // Transcription was turned on externally
      setState(prev => ({
        ...prev,
        isTranscribing: true,
        error: null,
      }));
    }
  }, [derivedIsTranscribing, state.isTranscribing]);

  // Subscribe to transcription events using the Chime SDK
  useEffect(() => {
    // If transcription is not enabled or not transcribing, don't subscribe to transcription events
    if (isTranscriptionDisabled || !state.isTranscribing) {
      return;
    }

    if (!audioVideo) {
      return;
    }

    // Check if transcription controller is available
    if (audioVideo.transcriptionController) {
      const transcriptCallback = (transcriptEvent: TranscriptEvent) => {
        if (!(transcriptEvent instanceof Transcript)) return;
        if (!transcriptEvent.results || transcriptEvent.results.length === 0) return;

        const result = transcriptEvent.results[0];
        const alternative = result.alternatives?.[0];
        if (!alternative) return;
        const text = alternative.transcript.trim();
        const attendee = alternative.items?.[0]?.attendee || null;
        const speakerId = attendee?.externalUserId || "unknown";
        const { displayName: speakerName } = attendee
          ? extractRoomMemberData(attendee)
          : { displayName: "Speaker" };

        const timestamp = Date.now();
        if (!result.isPartial) {
          const confidence =
            alternative.items && alternative.items.length > 0
              ? alternative.items[0].confidence || 0
              : 0;

          if (confidence > TRANSCRIPTION_CONFIDENCE_THRESHOLD && text.length > 0) {
            const newSegment: TranscriptionSegment = {
              id: timestamp.toString(),
              text,
              speakerId,
              speakerName,
              startTime: timestamp,
              endTime: timestamp,
              isPartial: false,
              confidence,
            };

            const line = `${speakerId}: ${text}`;

            setState((prev) => ({
              ...prev,
              currentSegment: newSegment,
              segments: [...prev.segments, newSegment],
              lines: [...prev.lines, line],
            }));

            // Reset partial tracking
            lastPartialText.current = null;
            lastPartialUpdate.current = 0;
          }
        } else {
          // Skip short/empty partials
          if (text.length < 6) return;

          const didChange = text !== lastPartialText.current;
          const now = Date.now();
          const timeSinceLast = now - lastPartialUpdate.current;

          if (didChange && timeSinceLast > TRANSCRIPTION_PARTIAL_DEBOUNCE_MS) {
            lastPartialText.current = text;
            lastPartialUpdate.current = now;

            const partialSegment: TranscriptionSegment = {
              id: `partial-${now}`,
              text,
              speakerId,
              speakerName,
              startTime: now,
              endTime: now,
              isPartial: true,
              confidence: 0,
            };

            setState((prev) => ({
              ...prev,
              currentSegment: partialSegment,
            }));
          }
        }
      };

      // Subscribe to transcript events
      audioVideo.transcriptionController.subscribeToTranscriptEvent(
        transcriptCallback
      );

      return () => {
        // Unsubscribe from transcript events
        if (audioVideo.transcriptionController) {
          audioVideo.transcriptionController.unsubscribeFromTranscriptEvent(
            transcriptCallback
          );
        }
      };
    }
  }, [audioVideo, isTranscriptionDisabled, state.isTranscribing]);

  // Toggle transcription function
  const toggleTranscription = useCallback(async () => {
    if (isToggling.current) return;
    if (!isTranscriptionAvailable || !meetingDetails?.id) {
      setState(prev => ({
        ...prev,
        error: 'Transcription not available for this meeting',
      }));
      return;
    }

    // If transcription is currently on, just toggle local state (turn off subtitles)
    if (state.isTranscribing) {
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        error: null,
      }));
    } else {
      // If transcription is off, check if it's already on via ccStatus
      if (transcriptionStatus === 'on') {
        // Already on, just toggle local state to show subtitles
        setState(prev => ({
          ...prev,
          isTranscribing: true,
          error: null,
        }));
      } else {
        // Turn on transcription via API
        try {
          isToggling.current = true;
          setState(prev => ({ ...prev, isLoading: true, error: null }));
          const response = await toggleVideoMeetingTranscription(meetingDetails.id);

          // If API call was successful, immediately update local state
          if (response.transcription_status === 'on') {
            setState(prev => ({
              ...prev,
              isTranscribing: true,
              isLoading: false,
              error: null,
            }));

            // Also update the global transcription status in Zustand
            setTranscriptionStatus('on');
          } else {
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: 'Transcription started but status is not on',
            }));
          }
        } catch (error) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: 'Failed to turn on transcription',
          }));
          logVideoRoomError(
            "Failed to toggle transcription",
            "transcription_toggle_error",
            { meetingId: meetingDetails?.id, error: String(error) }
          );
        } finally {
          isToggling.current = false;
        }
      }
    }
  }, [isTranscriptionAvailable, meetingDetails?.id, state.isTranscribing, transcriptionStatus, setTranscriptionStatus]);

  const contextValue: TranscriptionContextType = {
    state: {
      ...state,
      isTranscriptionDisabled,
    },
    toggleTranscription,
    isTranscriptionAvailable,
  };

  return (
    <TranscriptionContext.Provider value={contextValue}>
      {children}
    </TranscriptionContext.Provider>
  );
};

export const useTranscription = (): TranscriptionContextType => {
  const context = useContext(TranscriptionContext);
  if (!context) {
    throw new Error(
      "useTranscription must be used within TranscriptionProvider"
    );
  }
  return context;
};